import React from 'react'

import InfinityUpdate from './InfinityUpdate'
import { FeedInPowerModel } from '../model'
import FeedInPowerData from './FeedInPowerData'
import { useActiveInstallationContext } from 'src/Libs/installations'
import { FarmMember } from 'src/Elements/Farm/types'
import { SmartMeterGatewayInfoPanel, SmartMeterGatewayPanel } from 'src/Libs/installations/components/new-installation/configuration/em360'

interface Props {
  storageId?: string
  isReadOnlyAccess?: boolean
  isInfinityUpdatePossible: boolean
  isRunInfinityUpdateLoading: boolean
  runInfinityUpdate: () => void
  feedInPowerData?: FeedInPowerModel
  getFeedInPowerData: (storageId?: string) => Promise<void>
  isFeedInPowerDataLoading: boolean
  isFarmMasterSelected?: boolean
  isFarm: boolean
  farmClients?: FarmMember[]
  refetchSmartMeterGatewayData?: () => Promise<void>
  isFeedInPowerError: boolean
}

/**
 * Wraps the installation settings in an accordion
 */
const InstallationSettings: React.FC<Props> = ({
  feedInPowerData,
  getFeedInPowerData,
  isInfinityUpdatePossible,
  isRunInfinityUpdateLoading,
  runInfinityUpdate,
  storageId,
  isReadOnlyAccess = true,
  isFeedInPowerDataLoading,
  isFarmMasterSelected,
  isFarm,
  farmClients,
  refetchSmartMeterGatewayData,
  isFeedInPowerError,
}) => {
  const { installationId, deviceLinks, installation } = useActiveInstallationContext()
  const emcTypeByMasterProduct = installation?.emsMasterProduct === 'qntm' ? 'emc' : 'storage'
  const emcDeviceLink = deviceLinks.find(deviceLink => deviceLink.deviceType === emcTypeByMasterProduct)
  const doesOneClientHaveSmgwActivated = farmClients?.some(client => client.isSmgwActive)
  const doesCurrentClientHaveSmgwActivated = farmClients?.find(client => client.storageId === storageId)?.isSmgwActive

  const farmMasterContent = [
    <FeedInPowerData
      key={'feed-in-power-data'}
      storageId={storageId}
      isReadOnlyAccess={isReadOnlyAccess}
      feedInPowerData={feedInPowerData}
      isFeedInPowerDataLoading={isFeedInPowerDataLoading}
      isFeedInPowerError={isFeedInPowerError}
      getFeedInPowerData={getFeedInPowerData}
    />,
    <SmartMeterGatewayInfoPanel key={'smart-meter-gateway-info-panel'} />,
  ]

  const clientContent = [
    <FeedInPowerData
      key={'feed-in-power-data'}
      storageId={storageId}
      isReadOnlyAccess={isReadOnlyAccess}
      feedInPowerData={feedInPowerData}
      isFeedInPowerDataLoading={isFeedInPowerDataLoading}
      isFeedInPowerError={isFeedInPowerError}
      getFeedInPowerData={getFeedInPowerData}
    />,
    <SmartMeterGatewayPanel
      key={'smart-meter-gateway-panel'}
      installationId={installationId}
      storageId={storageId}
      emcDeviceLink={emcDeviceLink}
      isSettingsPage={true}
      isFarm={isFarm}
      isDisabled={doesOneClientHaveSmgwActivated && !doesCurrentClientHaveSmgwActivated}
      refetchSmartMeterGatewayDataForFarms={refetchSmartMeterGatewayData}
      isReadOnlyAccess={isReadOnlyAccess}
      systemSkiLabel={'systemSettings.installation.settings.smart-meter-gateway.ski.title'}
    />,
    <InfinityUpdate
      key={'infinity-update'}
      isInfinityUpdatePossible={isInfinityUpdatePossible}
      isRunInfinityUpdateLoading={isRunInfinityUpdateLoading}
      runInfinityUpdate={runInfinityUpdate}
    />,
  ]

  if (isFarm) {
    return isFarmMasterSelected ? farmMasterContent : clientContent
  }

  return clientContent
}

export default InstallationSettings
